import { ApiResult, HttpClient } from "../http.js";

/** GET /api/integrations/danlon/install-url response. */
export interface PayrollInstallUrlDto {
  url: string;
}

/** A Danløn employee not yet linked to a MesterPlan user. */
export interface PayrollEmployeeDto {
  externalId: string;
  name: string;
  email?: string | null;
  active: boolean;
  salary?: number | null;
  hourlyRate?: number | null;
  salaryType?: string | null;
  pensionEmployer?: number | null;
  pensionEmployee?: number | null;
  hoursPerWeek?: number | null;
  jobTitle?: string | null;
}

/** A Danløn employee already linked to a MesterPlan user. */
export interface MatchedPayrollEmployeeDto {
  externalId: string;
  name: string;
  email?: string | null;
  mpiUserId: string;
  mpiUserName: string;
  matchedBy: string;
}

/** GET /api/integrations/danlon/employees response. */
export interface PayrollEmployeeMappingOverviewDto {
  matched: MatchedPayrollEmployeeDto[];
  unmatched: PayrollEmployeeDto[];
}

/** A Danløn pay-part code available for the code-mapping selector. */
export interface PayPartCodeDto {
  code: string;
  description: string;
  unitsAllowed: boolean;
  rateAllowed: boolean;
  amountAllowed: boolean;
}

/**
 * Maps a MesterPlan source type to a Danløn pay-part code. `mpiType` is a
 * `TimeEntryType` name (Work/Travel/…) or the `LeaveType` name "Sick".
 */
export interface PayPartCodeMappingDto {
  mpiType: string;
  danlonPayPartCode: string;
  danlonPayPartDescription: string;
}

export interface SaveCodeMappingPayload {
  mapping: PayPartCodeMappingDto[];
}

/**
 * One manual employee-mapping edit. A missing/empty `externalEmployeeId`
 * deletes any existing mapping for `mpiUserId`; otherwise the row is upserted
 * as `matchedBy: "manual"` server-side (not settable from the client).
 */
export interface EmployeeMappingEntryDto {
  mpiUserId: string;
  externalEmployeeId?: string | null;
  externalEmployeeName?: string | null;
}

export interface SaveEmployeeMappingsPayload {
  entries: EmployeeMappingEntryDto[];
}

export interface SaveEmployeeMappingsResultDto {
  upserted: number;
  deleted: number;
}

export interface SyncSalaryResultDto {
  matchedCount: number;
  unmatchedCount: number;
  unmatched: PayrollEmployeeDto[];
}

export type PayrollExportStatus = "Success" | "Partial" | "Failed";

export interface PushPayPartsResultDto {
  status: PayrollExportStatus;
  employeeCount: number;
  payPartCount: number;
  warnings: string[];
  exportId: string;
}

export interface PayrollExportDto {
  id: string;
  exportedByUserId: string;
  /** ISO 8601 instant. */
  periodStart: string;
  /** ISO 8601 instant. */
  periodEnd: string;
  employeeCount: number;
  payPartCount: number;
  status: PayrollExportStatus;
  errorsJson?: string | null;
  /** ISO 8601 instant. */
  createdAt: string;
}

export interface ExportPayrollPayload {
  /** ISO 8601 instant — send UTC midnight of the selected date. */
  periodStart: string;
  /** ISO 8601 instant — send UTC midnight of the selected date. */
  periodEnd: string;
}

/**
 * `/api/integrations/danlon/*` — the Danløn payroll integration. Wraps
 * `PayrollAuthController` (OAuth install/disconnect) + `PayrollController`
 * (employee/code mapping, sync, export) on the .NET API.
 */
export class PayrollApi {
  constructor(private readonly http: HttpClient) {}

  /**
   * GET /api/integrations/danlon/install-url
   * Returns the Danløn install URL including a signed state parameter. The
   * caller should redirect `window.location.href` to the returned URL.
   */
  getInstallUrl(): Promise<ApiResult<PayrollInstallUrlDto>> {
    return this.http.get<PayrollInstallUrlDto>("/api/integrations/danlon/install-url");
  }

  /**
   * POST /api/integrations/danlon/disconnect
   * Disconnects the Danløn integration for the caller's active org. Best-effort
   * revokes the refresh token, deletes it from Key Vault, and marks the
   * integration as disconnected. Idempotent (204 even if already disconnected).
   */
  disconnect(): Promise<ApiResult<void>> {
    return this.http.post<void>("/api/integrations/danlon/disconnect");
  }

  /** GET /api/integrations/danlon/employees — split into matched/unmatched. */
  getEmployees(): Promise<ApiResult<PayrollEmployeeMappingOverviewDto>> {
    return this.http.get<PayrollEmployeeMappingOverviewDto>("/api/integrations/danlon/employees");
  }

  /** GET /api/integrations/danlon/pay-part-codes */
  getPayPartCodes(): Promise<ApiResult<PayPartCodeDto[]>> {
    return this.http.get<PayPartCodeDto[]>("/api/integrations/danlon/pay-part-codes");
  }

  /** GET /api/integrations/danlon/code-mapping — the org's current mapping (prefill for the UI). */
  getCodeMapping(): Promise<ApiResult<PayPartCodeMappingDto[]>> {
    return this.http.get<PayPartCodeMappingDto[]>("/api/integrations/danlon/code-mapping");
  }

  /** PUT /api/integrations/danlon/code-mapping — replaces the org's full mapping. */
  saveCodeMapping(mapping: PayPartCodeMappingDto[]): Promise<ApiResult<PayPartCodeMappingDto[]>> {
    return this.http.put<PayPartCodeMappingDto[]>("/api/integrations/danlon/code-mapping", {
      body: { mapping } satisfies SaveCodeMappingPayload,
    });
  }

  /** PUT /api/integrations/danlon/employee-mappings — bulk link/unlink edits. */
  saveEmployeeMappings(
    entries: EmployeeMappingEntryDto[],
  ): Promise<ApiResult<SaveEmployeeMappingsResultDto>> {
    return this.http.put<SaveEmployeeMappingsResultDto>("/api/integrations/danlon/employee-mappings", {
      body: { entries } satisfies SaveEmployeeMappingsPayload,
    });
  }

  /** POST /api/integrations/danlon/sync-salary — auto-matches by email + writes salaries. */
  syncSalary(): Promise<ApiResult<SyncSalaryResultDto>> {
    return this.http.post<SyncSalaryResultDto>("/api/integrations/danlon/sync-salary");
  }

  /** GET /api/integrations/danlon/exports — recent export audit trail. */
  getExports(): Promise<ApiResult<PayrollExportDto[]>> {
    return this.http.get<PayrollExportDto[]>("/api/integrations/danlon/exports");
  }

  /**
   * POST /api/integrations/danlon/export
   * Pushes the given period's approved hours + approved sick leave to Danløn
   * as pay parts. `periodStart`/`periodEnd` must be ISO instants — send UTC
   * midnight of the selected local dates, since the backend converts via UTC
   * and a local-midnight string can shift the boundary by a day.
   */
  export(payload: ExportPayrollPayload): Promise<ApiResult<PushPayPartsResultDto>> {
    return this.http.post<PushPayPartsResultDto>("/api/integrations/danlon/export", { body: payload });
  }
}
